// ==========================================================================
// screens/Money.tsx — Money: budget codes, trackers, accounts (9 Oct 2026)
//
//   Budget codes: a Xero-style chart, grouped by the hundreds, with a search,
//     type filters, an archived view, and a side panel to add or edit. Each
//     code can carry a monthly budget and an ATO hint. The bar on top shows
//     how the monthly spending budget splits across the groups.
//   Trackers: cost trackers that cut across codes ("Isadora — baby").
//   Accounts: banks, cards, loans and brokers, grouped by who owns them.
//   Reconcile: Xero-style cards with OK, buckets and rules (FF-039).
//   Transactions / Import: bank statement CSVs, coded to the above (FF-010).
// Spending against budgets and trackers comes from coded transactions.
// ==========================================================================
import QueryUnavailable from "@/components/QueryUnavailable";
import { useMemo, useState, type FormEvent } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Archive, ArchiveRestore, Banknote, CreditCard, Landmark, LineChart, PiggyBank, Plus, Search, Wallet } from "lucide-react";
import { PageHeader } from "@/components/Page";
import { Button, Card, ErrorText } from "@/components/ui";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { tokenColour } from "@/lib/calendar";
import { useEntities } from "@/lib/household";
import { useSplitTotals } from "@/lib/transactions";
import MoneyTransactions from "./MoneyTransactions";
import MoneyReconcile, { RulesList } from "./MoneyReconcile";
import MoneyDashboard from "./MoneyDashboard";
import MoneyIncome from "./MoneyIncome";
import MoneyContacts from "./MoneyContacts";
import MoneyReceipts from "./MoneyReceipts";
import MoneyCheck from "./MoneyCheck";
import { useCards, useSaveCard } from "@/lib/contacts";
import { usePeople } from "@/lib/household";
import MoneyImport from "./MoneyImport";
import { endOfMonth, startOfMonth, subMonths } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useHouseholdId } from "@/lib/household";
import { fyOf, fyRange } from "@/lib/vehicles";
import { splitDescription } from "@/lib/transactions";
import { useContacts } from "@/lib/contacts";
import { LOAN_STYLES, balanceOf } from "@/lib/loans";
import { useRecSummary, useSaveStatement } from "@/lib/reconcile";
import {
  ACCOUNT_KINDS, KINDS, fromCents, money, toCents, useAccounts, useCategories, useRemoveAccount, useSaveAccount, useSaveCategory, useSaveTracker, useTrackers,
  type Account, type AccountKind, type Category, type Kind, type Tracker,
} from "@/lib/money";

const GROUP_COLOURS = ["chart-1", "chart-4", "chart-3", "chart-2", "chart-5", "chart-6"];

// ---------------------------------------------------------------------------
// Budget codes
// ---------------------------------------------------------------------------
function CodeSheet({ cat, all, onClose }: { cat?: Category; all: Category[]; onClose: () => void }) {
  const save = useSaveCategory();
  const groups = all.filter((c) => c.is_group && !c.archived_at);
  const [isGroup, setIsGroup] = useState(cat?.is_group ?? false);
  const [code, setCode] = useState(cat?.code ?? "");
  const [name, setName] = useState(cat?.name ?? "");
  const [parent, setParent] = useState(cat?.parent_id ?? groups[0]?.id ?? "");
  const [kind, setKind] = useState<Kind>(cat?.kind ?? "expense");
  const [tax, setTax] = useState(cat?.tax_label ?? "");
  const [budget, setBudget] = useState(fromCents(cat?.monthly_budget_cents ?? null));
  const [desc, setDesc] = useState(cat?.description ?? "");
  const parentKind = groups.find((g) => g.id === parent)?.kind;
  const taken = !cat && all.some((c) => c.code === code);

  async function submit(e: FormEvent) {
    e.preventDefault();
    await save.mutateAsync({ id: cat?.id, c: {
      code, name: name.trim(), is_group: isGroup, parent_id: isGroup ? null : parent || null,
      kind: isGroup ? kind : parentKind ?? kind, tax_label: tax.trim() || null,
      monthly_budget_cents: isGroup ? null : toCents(budget), description: desc.trim() || null,
    } });
    onClose();
  }

  return (
    <Sheet title={cat ? `${cat.code} · ${cat.name}` : "New budget code"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {!cat && (
          <div className="flex rounded-full bg-surface-raised p-1 text-sm">
            {[false, true].map((g) => (
              <button key={String(g)} type="button" onClick={() => setIsGroup(g)} className={`flex-1 rounded-full py-1.5 ${isGroup === g ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}>{g ? "Group" : "Code"}</button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[110px_1fr] gap-3">
          <Lbl label="Code"><input required inputMode="numeric" pattern="[0-9]{3,4}" disabled={!!cat} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} className={`${inputCls} num`} placeholder="540" /></Lbl>
          <Lbl label="Name"><input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Swimming lessons" /></Lbl>
        </div>
        {taken && <ErrorText>Code {code} is already used.</ErrorText>}
        {isGroup ? (
          <Lbl label="Type">
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} className={inputCls}>{KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}</select>
          </Lbl>
        ) : (
          <Lbl label="Group" hint={parentKind ? `Type comes from the group: ${KINDS.find((k) => k.key === parentKind)?.label}` : undefined}>
            <select value={parent} onChange={(e) => setParent(e.target.value)} className={inputCls}>{groups.map((g) => <option key={g.id} value={g.id}>{g.code} · {g.name}</option>)}</select>
          </Lbl>
        )}
        {!isGroup && (
          <Lbl label="Monthly budget" hint="Optional. Leave blank to track without a budget.">
            <div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary">
              <span className="text-ink-muted">$</span>
              <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" placeholder="0" />
              <span className="text-sm text-ink-muted">/ month</span>
            </div>
          </Lbl>
        )}
        <Lbl label="ATO hint" hint="Optional. Where this lands at tax time, e.g. D5 other work-related."><input value={tax} onChange={(e) => setTax(e.target.value)} className={inputCls} /></Lbl>
        <Lbl label="Notes"><textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} /></Lbl>
        <ErrorText>{save.error ? "Couldn't save. Check the code isn't already used." : null}</ErrorText>
        <Button type="submit" disabled={save.isPending || taken}>{save.isPending ? "Saving…" : cat ? "Save changes" : "Add"}</Button>
        {cat && (
          <Button type="button" variant="ghost" onClick={async () => { await save.mutateAsync({ id: cat.id, c: { archived_at: cat.archived_at ? null : new Date().toISOString() } }); onClose(); }}>
            <span className="flex items-center justify-center gap-2">{cat.archived_at ? <><ArchiveRestore className="h-4 w-4" /> Restore</> : <><Archive className="h-4 w-4" /> Archive</>}</span>
          </Button>
        )}
      </form>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Periods for the spend column and the code ledger (9 Oct 2026).
// Australian financial year: 1 July to 30 June.
// ---------------------------------------------------------------------------
type PeriodKey = "month" | "last_month" | "fy" | "last_fy" | "all";
const PERIOD_KEYS: PeriodKey[] = ["month", "last_month", "fy", "last_fy", "all"];
function periodRange(k: PeriodKey, now: Date): { from: string; to: string; label: string } {
  const ymd = (d: Date) => format(d, "yyyy-MM-dd");
  const fy = fyOf(now); const fyLast = `${Number(fy.slice(0, 4)) - 1}-${fy.slice(2, 4)}`;
  if (k === "month") return { from: ymd(startOfMonth(now)), to: ymd(endOfMonth(now)), label: format(now, "MMMM") };
  if (k === "last_month") { const m = subMonths(now, 1); return { from: ymd(startOfMonth(m)), to: ymd(endOfMonth(m)), label: format(m, "MMMM") }; }
  if (k === "fy") return { ...fyRange(fy), label: `FY ${fy}` };
  if (k === "last_fy") return { ...fyRange(fyLast), label: `FY ${fyLast}` };
  return { from: "2000-01-01", to: "2100-12-31", label: "All time" };
}

/** Every line coded to a code (or a whole group), for a period. */
function useCodeLedger(ids: string[], from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["split_totals", hid, "ledger", ids.join(","), from, to], enabled: !!hid && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from("transaction_splits")
        .select("id,amount_cents,category_id,tracker_id,transactions!inner(id,posted_on,description,account_id,contact_id,deleted_at)")
        .in("category_id", ids).gte("transactions.posted_on", from).lte("transactions.posted_on", to).is("transactions.deleted_at", null).limit(2000);
      if (error) throw error;
      type Row = { id: string; amount_cents: number; category_id: string; tracker_id: string | null; transactions: { id: string; posted_on: string; description: string; account_id: string; contact_id: string | null } };
      return (data as unknown as Row[]).map((r) => ({ ...r, amount_cents: Number(r.amount_cents) })).sort((a, b) => b.transactions.posted_on.localeCompare(a.transactions.posted_on));
    },
  });
}

function LedgerSheet({ cat, all, period, onPeriod, onEdit, onClose }: { cat: Category; all: Category[]; period: PeriodKey; onPeriod: (p: PeriodKey) => void; onEdit: () => void; onClose: () => void }) {
  const now = new Date(); const range = periodRange(period, now);
  const ids = cat.is_group ? [cat.id, ...all.filter((c) => c.parent_id === cat.id).map((c) => c.id)] : [cat.id];
  const rows = useCodeLedger(ids, range.from, range.to); const accounts = useAccounts(); const contacts = useContacts();
  const sign = cat.kind === "income" ? 1 : -1;
  const total = (rows.data ?? []).reduce((s, r) => s + r.amount_cents, 0) * sign;
  const byMonth = new Map<string, number>();
  for (const r of rows.data ?? []) { const k = r.transactions.posted_on.slice(0, 7); byMonth.set(k, (byMonth.get(k) ?? 0) + r.amount_cents * sign); }
  return (
    <Sheet wide title={`${cat.code} · ${cat.is_group ? `All of ${cat.name}` : cat.name}`} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <select value={period} onChange={(e) => onPeriod(e.target.value as PeriodKey)} className="rounded-full bg-surface px-4 py-2 text-sm ring-1 ring-line">
            {PERIOD_KEYS.map((k) => <option key={k} value={k}>{periodRange(k, now).label}</option>)}
          </select>
          <div className="ml-auto text-right"><div className="num text-2xl font-semibold">{money(total, { cents: true })}</div><div className="text-xs text-ink-muted">{rows.data?.length ?? 0} lines · {cat.kind === "income" ? "received" : cat.kind === "transfer" ? "moved" : "spent"}</div></div>
          <button type="button" onClick={onEdit} className="rounded-full px-4 py-2 text-sm font-medium ring-1 ring-line">Edit code</button>
        </div>
        {byMonth.size > 1 && (
          <div className="flex flex-wrap gap-1.5">{[...byMonth.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([m, v]) => (
            <span key={m} className="rounded-full bg-surface-raised px-2.5 py-1 text-xs"><span className="font-medium">{format(parseISO(`${m}-01`), "MMM yy")}</span> <span className="num">{money(v)}</span></span>
          ))}</div>
        )}
        <Card className="overflow-hidden p-0">
          {rows.isLoading ? <p className="p-6 text-ink-muted">Loading…</p> : (rows.data ?? []).length === 0 ? <p className="p-6 text-center text-ink-muted">Nothing coded here for {range.label.toLowerCase() === "all time" ? "any date" : range.label}. Try another period.</p> : (
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-surface-raised/60 text-left text-xs text-ink-muted"><tr><th className="px-4 py-2 font-medium">Date</th><th className="px-2 py-2 font-medium">Bank line</th>{cat.is_group && <th className="px-2 py-2 font-medium">Code</th>}<th className="px-2 py-2 font-medium">Account</th><th className="px-4 py-2 text-right font-medium">Amount</th></tr></thead>
              <tbody>{rows.data!.map((r) => { const c = all.find((x) => x.id === r.category_id); const who = contacts.data?.find((x) => x.id === r.transactions.contact_id); return (
                <tr key={r.id} className="border-t border-line/60">
                  <td className="num whitespace-nowrap px-4 py-2 text-ink-muted">{format(parseISO(r.transactions.posted_on), "d MMM yyyy")}</td>
                  <td className="max-w-0 px-2 py-2"><div className="truncate" title={r.transactions.description}>{splitDescription(r.transactions.description).merchant}</div>{who && <div className="text-xs text-ink-muted">{who.name}</div>}</td>
                  {cat.is_group && <td className="whitespace-nowrap px-2 py-2 text-xs text-ink-muted"><span className="num">{c?.code}</span> {c?.name}</td>}
                  <td className="whitespace-nowrap px-2 py-2 text-ink-muted">{accounts.data?.find((a) => a.id === r.transactions.account_id)?.name}</td>
                  <td className="num whitespace-nowrap px-4 py-2 text-right font-medium">{money(r.amount_cents * sign, { cents: true })}</td>
                </tr>
              ); })}</tbody>
            </table>
          )}
        </Card>
      </div>
    </Sheet>
  );
}

function BudgetCodes() {
  const cats = useCategories();
  const all = cats.data ?? [];
  const [filter, setFilter] = useState<Kind | "all" | "archived">("all");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<Category | "new" | null>(null);
  const [ledger, setLedger] = useState<Category | null>(null);
  const now = new Date();
  const [period, setPeriod] = useState<PeriodKey>("fy");   // FY by default: a month hides most of the history
  const range = periodRange(period, now);
  const totals = useSplitTotals(range.from, range.to);
  // Spending shows as a positive number; income too. Sign follows the code's type.
  const spentOf = (c: Category) => { const v = totals.data?.byCat.get(c.id) ?? 0; return c.kind === "income" ? v : -v; };
  // A group's total includes lines coded straight to the group (a "bucket").
  const groupSpent = (g: Category) => spentOf(g) + all.filter((c) => c.parent_id === g.id).reduce((s, c) => s + spentOf(c), 0);

  const groups = all.filter((c) => c.is_group).sort((a, b) => a.code.localeCompare(b.code));
  const match = (c: Category) => {
    if (filter === "archived") { if (!c.archived_at) return false; }
    else { if (c.archived_at) return false; if (filter !== "all" && c.kind !== filter) return false; }
    const s = q.trim().toLowerCase();
    return !s || c.code.includes(s) || c.name.toLowerCase().includes(s) || (c.tax_label ?? "").toLowerCase().includes(s);
  };
  const budgetOf = (g: Category) => all.filter((c) => c.parent_id === g.id && !c.archived_at).reduce((s, c) => s + (c.monthly_budget_cents ?? 0), 0);
  const income = groups.filter((g) => g.kind === "income").reduce((s, g) => s + budgetOf(g), 0);
  const spendGroups = groups.filter((g) => g.kind === "expense");
  const spend = spendGroups.reduce((s, g) => s + budgetOf(g), 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_2fr]">
        <Card><div className="text-sm text-ink-muted">Income budget</div><div className="num mt-1 text-2xl font-semibold text-good">{money(income)}</div><div className="text-xs text-ink-muted">per month</div></Card>
        <Card><div className="text-sm text-ink-muted">Spending budget</div><div className="num mt-1 text-2xl font-semibold">{money(spend)}</div><div className="text-xs text-ink-muted">per month</div></Card>
        <Card><div className="text-sm text-ink-muted">Left over</div><div className={`num mt-1 text-2xl font-semibold ${income - spend < 0 ? "text-bad" : "text-primary"}`}>{money(income - spend)}</div><div className="text-xs text-ink-muted">per month</div></Card>
        <Card>
          <div className="mb-2 text-sm text-ink-muted">Where the spending budget goes</div>
          {spend === 0 ? <p className="text-sm text-ink-muted">Add monthly budgets to codes to see the split.</p> : (
            <>
              <div className="flex h-3 overflow-hidden rounded-full">
                {spendGroups.filter((g) => budgetOf(g) > 0).map((g, i) => <div key={g.id} title={`${g.name}: ${money(budgetOf(g))}`} style={{ width: `${(budgetOf(g) / spend) * 100}%`, background: tokenColour(GROUP_COLOURS[i % 6]) }} />)}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-muted">
                {spendGroups.filter((g) => budgetOf(g) > 0).map((g, i) => <span key={g.id} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: tokenColour(GROUP_COLOURS[i % 6]) }} />{g.name}</span>)}
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-full bg-surface-raised p-1 text-sm">
          {([["all", "All"], ["income", "Income"], ["expense", "Expenses"], ["transfer", "Money movements"], ["archived", "Archived"]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full px-3.5 py-1.5 ${filter === k ? "bg-surface font-semibold shadow-sm" : "text-ink-muted hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-full bg-surface px-4 py-2 ring-1 ring-line focus-within:ring-primary/60">
          <Search className="h-4 w-4 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name or ATO hint" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodKey)} aria-label="Spend period" className="rounded-full bg-surface px-4 py-2 text-sm ring-1 ring-line">
          {PERIOD_KEYS.map((k) => <option key={k} value={k}>{periodRange(k, now).label}</option>)}
        </select>
        <button type="button" onClick={() => setEdit("new")} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-soft"><Plus className="h-4 w-4" /> New code</button>
      </div>

      <Card className="overflow-hidden p-0">
        <table className="w-full text-left text-sm">
          <thead className="hidden border-b border-line bg-surface-raised/60 text-xs text-ink-muted lg:table-header-group">
            <tr><th className="w-20 px-5 py-2.5 font-medium">Code</th><th className="px-2 py-2.5 font-medium">Name</th><th className="px-2 py-2.5 font-medium">Type</th><th className="px-2 py-2.5 font-medium">ATO hint</th><th className="px-2 py-2.5 text-right font-medium">{range.label}</th><th className="px-5 py-2.5 text-right font-medium">Budget / month</th></tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const kids = all.filter((c) => c.parent_id === g.id && match(c));
              const showGroup = match(g) || kids.length > 0;
              if (!showGroup) return null;
              return [
                <tr key={g.id} onClick={() => setLedger(g)} className="cursor-pointer border-t border-line bg-surface-raised/40 first:border-t-0 hover:bg-surface-raised">
                  <td className="num px-5 py-2.5 font-semibold">{g.code}</td>
                  <td className="px-2 py-2.5 font-semibold" colSpan={1}><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: g.kind === "expense" ? tokenColour(GROUP_COLOURS[spendGroups.indexOf(g) % 6]) : g.kind === "income" ? tokenColour("success") : tokenColour("text-muted") }} />{g.name}{g.archived_at && <span className="ml-2 text-xs font-normal text-ink-muted">archived</span>}</td>
                  <td className="hidden px-2 py-2.5 text-ink-muted lg:table-cell">{KINDS.find((k) => k.key === g.kind)?.label}</td>
                  <td className="hidden px-2 py-2.5 lg:table-cell" />
                  <td className="num px-2 py-2.5 text-right font-semibold">{groupSpent(g) ? money(groupSpent(g)) : ""}</td>
                  <td className="num px-5 py-2.5 text-right font-semibold">{budgetOf(g) ? money(budgetOf(g)) : ""}</td>
                </tr>,
                ...kids.map((c) => (
                  <tr key={c.id} onClick={() => setLedger(c)} className={`cursor-pointer border-t border-line/60 hover:bg-primary/[0.04] ${c.archived_at ? "text-ink-muted" : ""}`}>
                    <td className="num px-5 py-2.5 pl-9 text-ink-muted">{c.code}</td>
                    <td className="px-2 py-2.5">{c.name}{c.archived_at && <span className="ml-2 text-xs text-ink-muted">archived</span>}<div className="text-xs text-ink-muted lg:hidden">{c.tax_label}</div></td>
                    <td className="hidden px-2 py-2.5 text-ink-muted lg:table-cell">{KINDS.find((k) => k.key === c.kind)?.label}</td>
                    <td className="hidden max-w-[280px] truncate px-2 py-2.5 text-ink-muted lg:table-cell" title={c.tax_label ?? ""}>{c.tax_label}</td>
                    <td className="num px-2 py-2.5 text-right">{spentOf(c) ? <span className={period === "month" && c.kind === "expense" && c.monthly_budget_cents && spentOf(c) > c.monthly_budget_cents ? "font-semibold text-bad" : ""}>{money(spentOf(c))}</span> : <span className="text-ink-muted/50">—</span>}</td>
                    <td className="num px-5 py-2.5 text-right">{c.monthly_budget_cents ? money(c.monthly_budget_cents) : <span className="text-ink-muted/50">—</span>}</td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-ink-muted">Codes are never deleted. Archive one you no longer use and it drops off the pickers but keeps its history. Click a code to see every line coded to it. The spend column follows the period picker; red means a spending code is over budget for the month.</p>

      {edit && <CodeSheet cat={edit === "new" ? undefined : edit} all={all} onClose={() => setEdit(null)} />}
      {ledger && <LedgerSheet cat={ledger} all={all} period={period} onPeriod={setPeriod} onEdit={() => { setEdit(ledger); setLedger(null); }} onClose={() => setLedger(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trackers
// ---------------------------------------------------------------------------
function TrackerSheet({ t, onClose }: { t?: Tracker; onClose: () => void }) {
  const save = useSaveTracker();
  const [name, setName] = useState(t?.name ?? "");
  const [colour, setColour] = useState(t?.colour ?? "chart-3");
  const [from, setFrom] = useState(t?.starts_on ?? "");
  const [to, setTo] = useState(t?.ends_on ?? "");
  const [budget, setBudget] = useState(fromCents(t?.budget_cents ?? null));
  const [notes, setNotes] = useState(t?.notes ?? "");
  async function submit(e: FormEvent) {
    e.preventDefault();
    await save.mutateAsync({ id: t?.id, t: { name: name.trim(), colour, starts_on: from || null, ends_on: to || null, budget_cents: toCents(budget), notes: notes.trim() || null } });
    onClose();
  }
  return (
    <Sheet title={t ? t.name : "New tracker"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Lbl label="Name"><input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Tasman purchase" /></Lbl>
        <Lbl label="Colour">
          <div className="flex gap-2">{["chart-1", "chart-2", "chart-3", "chart-4", "chart-5", "chart-6"].map((c) => (
            <button key={c} type="button" onClick={() => setColour(c)} aria-label={c} className={`h-8 w-8 rounded-full ${colour === c ? "ring-2 ring-ink ring-offset-2 ring-offset-surface" : ""}`} style={{ background: tokenColour(c) }} />
          ))}</div>
        </Lbl>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} /></Lbl>
          <Lbl label="To (optional)"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} /></Lbl>
        </div>
        <Lbl label="Budget (optional)">
          <div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" placeholder="0" /></div>
        </Lbl>
        <Lbl label="Notes"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></Lbl>
        <ErrorText>{save.error ? "Couldn't save. Try again." : null}</ErrorText>
        <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : t ? "Save changes" : "Add tracker"}</Button>
        {t && <Button type="button" variant="ghost" onClick={async () => { await save.mutateAsync({ id: t.id, t: { archived_at: t.archived_at ? null : new Date().toISOString() } }); onClose(); }}>{t.archived_at ? "Restore" : "Archive"}</Button>}
      </form>
    </Sheet>
  );
}

function Trackers() {
  const trackers = useTrackers();
  const totals = useSplitTotals("2000-01-01", "2100-12-31");
  const spent = (t: Tracker) => -(totals.data?.byTracker.get(t.id) ?? 0);
  const [edit, setEdit] = useState<Tracker | "new" | null>(null);
  const live = (trackers.data ?? []).filter((t) => !t.archived_at);
  const old = (trackers.data ?? []).filter((t) => t.archived_at);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-muted">A tracker follows one project across every budget code. Tag a purchase with it, whatever the code, and see the whole cost in one place: a baby, a car purchase, a renovation, a holiday.</p>
        <button type="button" onClick={() => setEdit("new")} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-soft"><Plus className="h-4 w-4" /> New tracker</button>
      </div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {live.map((t) => (
          <button key={t.id} type="button" onClick={() => setEdit(t)} className="overflow-hidden rounded-lg bg-surface text-left shadow-soft ring-1 ring-line/70 transition hover:ring-primary/40">
            <div className="h-2" style={{ background: tokenColour(t.colour) }} />
            <div className="space-y-4 p-5">
              <div>
                <div className="text-lg font-semibold">{t.name}</div>
                <div className="text-sm text-ink-muted">{t.starts_on ? `From ${format(parseISO(t.starts_on), "d MMM yyyy")}` : "No start date"}{t.ends_on ? ` to ${format(parseISO(t.ends_on), "d MMM yyyy")}` : ""}</div>
              </div>
              <div className="flex items-end justify-between">
                <div><div className="num text-3xl font-semibold" style={{ color: tokenColour(t.colour) }}>{money(spent(t))}</div><div className="text-xs text-ink-muted">spent so far</div></div>
                {t.budget_cents !== null && <div className="text-right"><div className="num font-semibold">{money(t.budget_cents)}</div><div className="text-xs text-ink-muted">budget</div></div>}
              </div>
              {t.budget_cents ? <div className="h-2 overflow-hidden rounded-full bg-surface-raised"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (spent(t) / t.budget_cents) * 100)}%`, background: spent(t) > t.budget_cents ? tokenColour("danger") : tokenColour(t.colour) }} /></div> : null}
              {t.notes && <p className="text-sm text-ink-muted">{t.notes}</p>}
            </div>
          </button>
        ))}
      </div>
      {old.length > 0 && <p className="text-sm text-ink-muted">Archived: {old.map((t) => <button key={t.id} type="button" onClick={() => setEdit(t)} className="mr-3 underline">{t.name}</button>)}</p>}
      <p className="text-xs text-ink-muted">Spent so far counts every coded transaction tagged with the tracker, across all dates.</p>
      {edit && <TrackerSheet t={edit === "new" ? undefined : edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
const kindIcon: Record<AccountKind, typeof Wallet> = { everyday: Wallet, savings: PiggyBank, offset: PiggyBank, credit_card: CreditCard, loan: Landmark, broker: LineChart, cash: Banknote, other: Wallet };

function AccountSheet({ a, onClose }: { a?: Account; onClose: () => void }) {
  const save = useSaveAccount(); const entities = useEntities(); const remove = useRemoveAccount();
  const [name, setName] = useState(a?.name ?? "");
  const [coOwner, setCoOwner] = useState(a?.co_owner_entity_id ?? "");
  const [kind, setKind] = useState<AccountKind>(a?.kind ?? "everyday");
  const [inst, setInst] = useState(a?.institution ?? "");
  const [owner, setOwner] = useState(a?.owner_entity_id ?? "");
  const [last4, setLast4] = useState(a?.last4 ?? "");
  const [ob, setOb] = useState(fromCents(a?.opening_balance_cents ?? null));
  const [obDate, setObDate] = useState(a?.opening_date ?? "");
  const [notes, setNotes] = useState(a?.notes ?? "");
  const [closed, setClosed] = useState(a?.closed_on ?? "");
  // Loans (FF-045): how it works, the limit and rate, and today's balance.
  const accounts = useAccounts(); const summary = useRecSummary(); const saveSt = useSaveStatement();
  const [style, setStyle] = useState<Account["loan_style"]>(a?.loan_style ?? null);
  const [limit, setLimit] = useState(fromCents(a?.credit_limit_cents ?? null));
  const [rate, setRate] = useState(a?.interest_rate_pct !== null && a?.interest_rate_pct !== undefined ? String(a.interest_rate_pct) : "");
  const [rateAt, setRateAt] = useState(a?.rate_as_at ?? "");
  const [repay, setRepay] = useState<Account["repayment_type"]>(a?.repayment_type ?? null);
  const [minRepay, setMinRepay] = useState(fromCents(a?.min_repayment_cents ?? null));
  const [offsetFor, setOffsetFor] = useState(a?.offset_for_account_id ?? "");
  const now = a ? balanceOf(a, summary.data) : null;
  const [bal, setBal] = useState(""); const [balAt, setBalAt] = useState(format(new Date(), "yyyy-MM-dd"));
  const loans = (accounts.data ?? []).filter((x) => x.kind === "loan" && x.id !== a?.id && !x.closed_on);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const isLoan = kind === "loan";
    await save.mutateAsync({ id: a?.id, a: { name: name.trim(), kind, institution: inst.trim() || null, owner_entity_id: owner || null, co_owner_entity_id: (owner && coOwner !== owner && coOwner) || null, last4: last4 || null, opening_balance_cents: toCents(ob), opening_date: obDate || null, notes: notes.trim() || null, closed_on: closed || null,
      loan_style: isLoan ? style : null, credit_limit_cents: isLoan ? toCents(limit) : null, interest_rate_pct: isLoan && rate.trim() ? Number(rate) : null, rate_as_at: isLoan ? rateAt || null : null,
      repayment_type: isLoan ? repay : null, min_repayment_cents: isLoan ? toCents(minRepay) : null, offset_for_account_id: kind === "offset" ? offsetFor || null : null } });
    // A balance typed here is saved as a statement balance (loans as money owed).
    const c = toCents(bal);
    if (a && c !== null && balAt) await saveSt.mutateAsync({ accountId: a.id, date: balAt, cents: kind === "loan" ? -Math.abs(c) : c, note: "Typed in Accounts" });
    onClose();
  }
  return (
    <Sheet title={a ? a.name : "New account"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Name"><input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Everyday" /></Lbl>
          <Lbl label="Bank or provider"><input value={inst} onChange={(e) => setInst(e.target.value)} className={inputCls} placeholder="NAB" /></Lbl>
        </div>
        <Lbl label="Type"><div className="flex flex-wrap gap-2">{ACCOUNT_KINDS.map((k) => (
          <button key={k.key} type="button" onClick={() => setKind(k.key)} className={`rounded-full px-3 py-1.5 text-sm ${kind === k.key ? "bg-primary font-semibold text-white" : "bg-surface-raised"}`}>{k.label}</button>
        ))}</div></Lbl>
        <Lbl label="Owner" hint="Whose account it is legally. Decides whose tax its interest and costs belong to.">
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className={inputCls}>
            <option value="">Not set</option>
            {entities.data?.map((en) => <option key={en.id} value={en.id}>{en.name}{en.is_external ? " (external)" : ""}</option>)}
          </select>
        </Lbl>
        <Lbl label="Joint with (optional)" hint="For a joint account, like the ING shared account. Interest and costs split between the two.">
          <select value={coOwner} onChange={(e) => setCoOwner(e.target.value)} className={inputCls}>
            <option value="">No one — not joint</option>
            {entities.data?.filter((en) => en.kind === "individual" && en.id !== owner).map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
          </select>
        </Lbl>
        <Lbl label="Account number, last 4 (optional)" hint="The account's own number, not a card. Cards go in Cards below. Full numbers are never stored."><input inputMode="numeric" value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} className={`${inputCls} num`} /></Lbl>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Opening balance"><div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={ob} onChange={(e) => setOb(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" /></div></Lbl>
          <Lbl label="as at"><input type="date" value={obDate} onChange={(e) => setObDate(e.target.value)} className={inputCls} /></Lbl>
        </div>
        {kind === "loan" && (
          <div className="space-y-4 rounded-lg bg-surface-raised/60 p-4">
            <Lbl label="How the loan works">
              <div className="flex flex-wrap gap-2">{LOAN_STYLES.map((k) => (
                <button key={k.key} type="button" onClick={() => setStyle(k.key)} className={`rounded-full px-3 py-1.5 text-sm ${style === k.key ? "bg-primary font-semibold text-white" : "bg-surface ring-1 ring-line"}`}>{k.label}</button>
              ))}</div>
            </Lbl>
            {style && <p className="-mt-2 text-xs text-ink-muted">{LOAN_STYLES.find((k) => k.key === style)?.hint}{style === "offset" ? " Then open the offset account and pick this loan under Sits against." : ""}</p>}
            <div className="grid grid-cols-2 gap-3">
              <Lbl label={style === "redraw" ? "Limit" : "Loan amount"} hint={style === "redraw" ? "Credit limit on the statement" : undefined}><div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" /></div></Lbl>
              <Lbl label="Repayments"><select value={repay ?? ""} onChange={(e) => setRepay((e.target.value || null) as Account["repayment_type"])} className={inputCls}><option value="">Not set</option><option value="pi">Principal and interest</option><option value="io">Interest only</option></select></Lbl>
              <Lbl label="Interest rate (% a year)"><input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value.replace(/[^0-9.]/g, ""))} className={`${inputCls} num`} placeholder="6.19" /></Lbl>
              <Lbl label="Rate since"><input type="date" value={rateAt} onChange={(e) => setRateAt(e.target.value)} className={inputCls} /></Lbl>
              <Lbl label="Minimum repayment (month)"><div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={minRepay} onChange={(e) => setMinRepay(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" /></div></Lbl>
            </div>
          </div>
        )}
        {kind === "offset" && (
          <Lbl label="Sits against" hint="The loan this offset reduces the interest on.">
            <select value={offsetFor} onChange={(e) => setOffsetFor(e.target.value)} className={inputCls}>
              <option value="">Not linked to a loan</option>
              {loans.map((l) => <option key={l.id} value={l.id}>{l.name}{l.institution ? ` (${l.institution})` : ""}</option>)}
            </select>
          </Lbl>
        )}
        {a && (
          <div className="space-y-2">
            <p className="text-sm text-ink-muted">Balance{kind === "loan" ? " owing" : ""}: <span className="num font-medium text-ink">{now ? money(now.cents, { cents: true }) : "not known yet"}</span>{now?.asAt ? ` as at ${format(parseISO(now.asAt), "d MMM yyyy")}` : ""}</p>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label={kind === "loan" ? "New balance owing (optional)" : "New balance (optional)"} hint="From the latest statement. Saved as a statement balance."><div className="flex items-center rounded-md border border-line bg-bg px-3 focus-within:border-primary"><span className="text-ink-muted">$</span><input inputMode="decimal" value={bal} onChange={(e) => setBal(e.target.value)} className="num w-full bg-transparent px-1 py-2.5 outline-none" /></div></Lbl>
              <Lbl label="as at"><input type="date" value={balAt} onChange={(e) => setBalAt(e.target.value)} className={inputCls} /></Lbl>
            </div>
          </div>
        )}
        <Lbl label="Notes"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></Lbl>
        <Lbl label="Closed on (optional)"><input type="date" value={closed} onChange={(e) => setClosed(e.target.value)} className={inputCls} /></Lbl>
        <ErrorText>{save.error ? "Couldn't save. Try again." : null}</ErrorText>
        <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : a ? "Save changes" : "Add account"}</Button>
        {a ? <CardsEditor account={a} /> : <p className="border-t border-line pt-4 text-xs text-ink-muted">Cards: add the account first, then open it again to add each person's card.</p>}
        {a && (
          <div className="space-y-2 border-t border-line pt-4">
            <p className="text-xs text-ink-muted">Closed it? Set "Closed on" above and it keeps its history. Added by mistake and never used? Remove it.</p>
            <Button type="button" variant="ghost" disabled={remove.isPending} onClick={async () => { await remove.mutateAsync(a.id); onClose(); }}>Remove (added by mistake)</Button>
            <ErrorText>{remove.error ? (remove.error as Error).message : null}</ErrorText>
          </div>
        )}
      </form>
    </Sheet>
  );
}

/** Cards on an account: who carries which card, so each line shows who spent it. */
function CardsEditor({ account }: { account: Account }) {
  const cards = useCards(); const save = useSaveCard(); const people = usePeople();
  const mine = (cards.data ?? []).filter((c) => c.account_id === account.id && !c.archived_at);
  const [who, setWho] = useState(""); const [last4, setLast4] = useState("");
  if (!["everyday", "credit_card", "offset", "savings"].includes(account.kind)) return null;
  return (
    <div className="space-y-2 border-t border-line pt-4">
      <div className="text-sm font-medium">Cards</div>
      <p className="text-xs text-ink-muted">The bank line ends with the card's last 4 digits (ING: "Card 462263xxxxxx4843"). Add each card and every line, old and new, shows who spent it.</p>
      {mine.map((c) => (
        <div key={c.id} className="flex items-center justify-between rounded-md bg-surface-raised px-3 py-2 text-sm">
          <span>{c.name} <span className="num text-ink-muted">·{c.last4}</span></span>
          <button type="button" onClick={() => save.mutate({ id: c.id, c: { archived_at: new Date().toISOString() } })} className="text-xs text-ink-muted hover:text-bad">Remove</button>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_100px_auto] gap-2">
        <select value={who} onChange={(e) => setWho(e.target.value)} className={inputCls}><option value="">Whose card?</option>{people.data?.filter((p) => p.role === "adult").map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}</select>
        <input inputMode="numeric" value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Last 4" className={`${inputCls} num`} />
        <button type="button" disabled={!who || last4.length !== 4 || save.isPending} onClick={async () => {
          const p = people.data?.find((x) => x.id === who);
          await save.mutateAsync({ c: { account_id: account.id, person_id: who, last4, name: `${p?.display_name ?? "Card"}'s card` } }); setWho(""); setLast4("");
        }} className="rounded-md bg-primary px-3 text-sm font-semibold text-white disabled:opacity-40">Add</button>
      </div>
      <ErrorText>{save.error ? (save.error as Error).message : null}</ErrorText>
    </div>
  );
}

function Accounts() {
  const accounts = useAccounts(); const entities = useEntities(); const cards = useCards(); const people = usePeople();
  const [edit, setEdit] = useState<Account | "new" | null>(null);
  const owners = useMemo(() => {
    const list = accounts.data ?? [];
    const keyOf = (a: Account) => [a.owner_entity_id ?? "", a.co_owner_entity_id ?? ""].filter(Boolean).sort().join("+");
    const ids = [...new Set(list.map(keyOf))];
    const name = (id: string) => entities.data?.find((e) => e.id === id)?.name;
    return ids.map((id) => {
      const parts = id.split("+").filter(Boolean);
      const entity = parts.length === 1 ? entities.data?.find((e) => e.id === parts[0]) : undefined;
      return { id, entity, label: parts.length > 1 ? `${parts.map(name).join(" & ")} — joint` : entity?.name, accounts: list.filter((a) => keyOf(a) === id) };
    }).sort((a, b) => Number(!!a.entity?.is_external) - Number(!!b.entity?.is_external) || (a.label ?? "zz").localeCompare(b.label ?? "zz"));
  }, [accounts.data, entities.data]);

  if (accounts.isError || entities.isError) return <QueryUnavailable name="Accounts" />;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-muted">Every bank account, card, loan and share account, grouped by who owns it. These came from last year's tax papers, so check each one. Add the NAB accounts when you open them.</p>
        <button type="button" onClick={() => setEdit("new")} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-soft"><Plus className="h-4 w-4" /> New account</button>
      </div>
      {owners.map((o) => (
        <section key={o.id}>
          <h2 className="mb-3 flex items-center gap-2 text-[17px] font-semibold">{o.label ?? "Owner not set"}{o.entity?.is_external && <span className="rounded-full bg-warn/10 px-2 py-0.5 text-xs font-medium text-warn">External</span>}</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {o.accounts.map((a) => {
              const Icon = kindIcon[a.kind];
              return (
                <button key={a.id} type="button" onClick={() => setEdit(a)} className={`flex items-start gap-4 rounded-lg bg-surface p-5 text-left shadow-soft ring-1 ring-line/70 transition hover:ring-primary/40 ${a.closed_on ? "opacity-60" : ""}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{a.name}</span>
                    <span className="block text-sm text-ink-muted">{[a.institution, ACCOUNT_KINDS.find((k) => k.key === a.kind)?.label].filter(Boolean).join(" · ")}{a.last4 && <span className="num"> · •••• {a.last4}</span>}</span>
                    {(cards.data ?? []).filter((c) => c.account_id === a.id && !c.archived_at).length > 0 && (
                      <span className="mt-1.5 flex flex-wrap gap-1">{(cards.data ?? []).filter((c) => c.account_id === a.id && !c.archived_at).map((c) => { const p = people.data?.find((x) => x.id === c.person_id); return (
                        <span key={c.id} className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ background: tokenColour(p?.colour ?? "chart-6") }}>{p?.display_name ?? "Card"} ·{c.last4}</span>
                      ); })}</span>
                    )}
                    {a.notes && <span className="mt-1 line-clamp-2 block text-xs text-ink-muted">{a.notes}</span>}
                    {a.closed_on && <span className="mt-1 block text-xs font-medium text-ink-muted">Closed {format(parseISO(a.closed_on), "MMM yyyy")}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {edit && <AccountSheet a={edit === "new" ? undefined : edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Settings() {
  const sub = ({ isActive }: { isActive: boolean }) => `border-b-2 px-1 pb-2 text-sm transition ${isActive ? "border-primary font-semibold text-primary" : "border-transparent text-ink-muted hover:text-ink"}`;
  return (
    <>
      <nav className="mb-6 flex flex-wrap gap-5 border-b border-line">
        <NavLink to="/money/settings/codes" className={sub}>Budget codes</NavLink>
        <NavLink to="/money/settings/contacts" className={sub}>Contacts</NavLink>
        <NavLink to="/money/settings/accounts" className={sub}>Accounts</NavLink>
        <NavLink to="/money/settings/trackers" className={sub}>Trackers</NavLink>
        <NavLink to="/money/settings/rules" className={sub}>Rules</NavLink>
        <NavLink to="/money/settings/check" className={sub}>Check</NavLink>
      </nav>
      <Routes>
        <Route path="codes" element={<BudgetCodes />} />
        <Route path="contacts" element={<MoneyContacts />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="trackers" element={<Trackers />} />
        <Route path="rules" element={<div className="max-w-2xl"><RulesList /></div>} />
        <Route path="check" element={<MoneyCheck />} />
        <Route path="*" element={<Navigate to="codes" replace />} />
      </Routes>
    </>
  );
}

// Money is the hub: Dashboard, Reconcile, Receipts (FF-044), Transactions, Import, and Settings
// underneath (codes, contacts, accounts, trackers, rules). 9 Oct 2026.
export default function Money() {
  const tab = ({ isActive }: { isActive: boolean }) => `rounded-full px-4 py-2 text-sm transition ${isActive ? "bg-ink text-bg font-semibold" : "text-ink-muted hover:text-ink"}`;
  return (
    <>
      <PageHeader title="Money" subtitle="Where it comes from, where it goes, and how it's tracking against the budget." />
      <nav className="mb-7 flex flex-wrap gap-2">
        <NavLink to="/money/dashboard" className={tab}>Dashboard</NavLink>
        <NavLink to="/money/income" className={tab}>Income & forecast</NavLink>
        <NavLink to="/money/reconcile" className={tab}>Reconcile</NavLink>
        <NavLink to="/money/receipts" className={tab}>Receipts</NavLink>
        <NavLink to="/money/transactions" className={tab}>Transactions</NavLink>
        <NavLink to="/money/import" className={tab}>Import</NavLink>
        <NavLink to="/money/settings" className={tab}>Settings</NavLink>
      </nav>
      <Routes>
        <Route path="dashboard" element={<MoneyDashboard />} />
        <Route path="income" element={<MoneyIncome />} />
        <Route path="reconcile" element={<MoneyReconcile />} />
        <Route path="receipts" element={<MoneyReceipts />} />
        <Route path="transactions" element={<MoneyTransactions />} />
        <Route path="shares" element={<Navigate to="/investments" replace />} />
        <Route path="import" element={<MoneyImport />} />
        <Route path="settings/*" element={<Settings />} />
        {/* old links */}
        <Route path="codes" element={<Navigate to="/money/settings/codes" replace />} />
        <Route path="trackers" element={<Navigate to="/money/settings/trackers" replace />} />
        <Route path="accounts" element={<Navigate to="/money/settings/accounts" replace />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </>
  );
}
